import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';

import QuickFiltersContainer from '../../../containers/quickFilters';
import useParamHotelId from '../../../hooks/router/params/useParamHotelId';
import useParamTopicId from '../../../hooks/router/params/useParamTopicId';
import useIsMobile from '../../../hooks/useIsMobile';
import translate from '../../../i18n/translate';

import { getTopicDetailsUrl } from '../../../routes/urls/topic';

import LoadingTopicBasic from '../../Loading/TopicBasic';
import ButtonIcon from '../ButtonIcon';
import LinkIcon, { NAMES } from '../LinkIcon';
import RichText from '../RichText';

import * as Styled from './styled';

const iconSize = 24;

export type Props = {
  description: string | null | undefined;
  isLoading: boolean;
  isSearchFeed?: boolean;
  title: string;
};

/**
 * Feed header component on the feed
 *
 * @param props              Props passed to the component
 * @param props.description  Topic description shown in the header
 * @param props.isLoading    Whether the data is being fetched
 * @param props.isSearchFeed Whether the FeedHeader is used for SearchFeed/SmartViews or not
 * @param props.title        Topic title shown in the header
 * @returns                  The component itself
 */
const FeedHeader: FC<Props> = ({
  description,
  isLoading,
  isSearchFeed = false,
  title,
}) => {
  const [quickFiltersVisible, setQuickFiltersVisible] = useState(true);
  const isMobile = useIsMobile();
  const location = useLocation();
  const topicId = useParamTopicId();
  const hotelId = useParamHotelId();

  if (isSearchFeed) {
    return (
      <Styled.Wrapper>
        <Styled.H2>{title}</Styled.H2>
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper>
      {!isMobile && (
        <Styled.TopicInfoWrapper>
          <LinkIcon
            color="var(--color-placeholder)"
            iconHeight={iconSize}
            iconName={NAMES.GENERAL__INFO__ALT}
            iconWidth={iconSize}
            label={translate('TOPIC__EDIT')}
            url={getTopicDetailsUrl(hotelId, location, topicId)}
          />
          {isLoading ? (
            <LoadingTopicBasic />
          ) : (
            <>
              <Styled.H2>
                <RichText>{title}</RichText>
              </Styled.H2>

              <Styled.TopicDescription>
                {description ?? ''}
              </Styled.TopicDescription>
            </>
          )}
          <ButtonIcon
            color="var(--color-icons-primary)"
            colorHover="var(--color-icons-hover)"
            iconHeight={30}
            iconName={NAMES.GENERAL__FILTER}
            iconWidth={30}
            label={translate('GENERAL__QUICK_FILTERS')}
            onClick={() => setQuickFiltersVisible(!quickFiltersVisible)}
          />
        </Styled.TopicInfoWrapper>
      )}
      {quickFiltersVisible && <QuickFiltersContainer />}
    </Styled.Wrapper>
  );
};

export default FeedHeader;
